import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PROJECTS } from "@/lib/content";
import { MaskLines, Reveal, FadeIn } from "@/components/Reveal";
import { BeforeAfter } from "@/components/BeforeAfter";
import { SEO } from "@/components/SEO";
import { useLang } from "@/i18n/LanguageContext";

export default function ProjectDetail() {
  const { slug } = useParams();
  const { c, lang } = useLang();
  const project = PROJECTS.find((p) => p.slug === slug);
  const next = PROJECTS[(PROJECTS.findIndex((p) => p.slug === slug) + 1) % PROJECTS.length];

  if (!project) {
    return (
      <div data-testid="project-not-found" className="min-h-screen flex flex-col items-center justify-center gap-6">
        <h1 className="font-serif text-4xl">{c.notFound}</h1>
        <Link to="/portfolio" className="btn-ink">{c.backPortfolio}</Link>
      </div>
    );
  }

  const meta = c.projects[project.slug];
  const story = c.projectStories && c.projectStories[project.slug];
  const p = c.pages.portfolio;

  return (
    <div data-testid="project-detail-page">
      <SEO title={`${project.name} — ${meta.type} | O'Lime Design`} description={(meta.intro || "").slice(0, 155)} path={`/portfolio/${project.slug}`} lang={lang} image={project.image} />
      <section className="relative h-[80vh] overflow-hidden grain">
        <img src={project.image} alt={project.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
        <div className="relative z-10 h-full container-olime flex flex-col justify-end pb-16 text-white">
          <FadeIn><span className="eyebrow text-white/70">{meta.type} • {project.year}</span></FadeIn>
          <h1 className="font-serif text-6xl md:text-8xl tracking-tight mt-4 !text-white"><MaskLines lines={[project.name]} base={0.2} /></h1>
          <FadeIn delay={0.5}><p className="mt-4 text-white/80">{project.location}</p></FadeIn>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-olime-bg">
        <div className="container-olime grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="space-y-6 text-sm">
                {[[p.lieu, project.location], [p.type, meta.type], [p.annee, project.year]].map(([k, v]) => (
                  <div key={k} className="border-t border-olime-line pt-3">
                    <span className="eyebrow block mb-1">{k}</span><span className="text-olime-ink text-base">{v}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8"><Reveal>
            <p className="font-serif text-3xl md:text-4xl leading-[1.3] tracking-tight">{meta.intro}</p>
            <Link to="/contact#form" data-testid="project-quote-cta" className="btn-ink mt-8">{c.cta.quote} <ArrowRight size={14} /></Link>
          </Reveal></div>
        </div>
      </section>

      {story && (
        <section data-testid="project-story" className="py-20 lg:py-28 bg-white">
          <div className="container-olime">
            <Reveal><span className="eyebrow">{story.label}</span></Reveal>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
              {story.chapters.map((ch, i) => (
                <Reveal key={i} delay={(i % 2) * 0.08}>
                  <div className="border-t border-olime-ink pt-6">
                    <span className="font-serif text-3xl text-olime-stone">0{i + 1}</span>
                    <h3 className="font-serif text-2xl md:text-3xl mt-2">{ch.title}</h3>
                    <p className="text-olime-muted mt-3 leading-relaxed">{ch.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {project.beforeAfter && (
        <section data-testid="project-before-after" className="py-20 lg:py-28 bg-white">
          <div className="container-olime">
            <Reveal><span className="eyebrow">{p.baTitle}</span></Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8">
                <BeforeAfter
                  before={project.beforeAfter.before}
                  after={project.beforeAfter.after}
                  beforeLabel={p.before}
                  afterLabel={p.after}
                  hint={p.baHint}
                  alt={project.name}
                />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {project.gallery.length > 0 && (
        <section className="pb-24 bg-olime-bg">
          <div className="container-olime grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {project.gallery.map((g, i) => (
              <Reveal key={i} delay={(i % 2) * 0.06} className={i === 0 ? "md:col-span-2" : ""}>
                <img src={g} alt={`${project.name} ${i + 1}`} loading="lazy" className="w-full h-auto object-cover" />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="py-16 bg-olime-ink text-white">
        <div className="container-olime flex items-center justify-between">
          <Link to="/portfolio" data-testid="back-to-portfolio" className="inline-flex items-center gap-2 link-underline text-sm uppercase tracking-[0.2em]"><ArrowLeft size={14} /> {c.cta.back}</Link>
          <Link to={`/portfolio/${next.slug}`} data-testid="next-project" className="inline-flex items-center gap-3 group">
            <span className="text-right"><span className="block eyebrow text-white/50">{c.cta.nextProject}</span><span className="font-serif text-2xl !text-white">{next.name}</span></span>
            <ArrowRight size={18} className="transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
